import { CronofyCalendarPermission } from '@/gql/cronofy/types';
import { useCronofyRequestExtendedPermissions } from '@/services/cronofy/hooks';
import { TFunction } from 'i18next';

export type ExtendedPermissionsResult = {
  success: boolean;
  automatic: boolean;
  error?: string;
};

export class CronofyExtendedPermissionsManager {
  private requestPermissions: ReturnType<typeof useCronofyRequestExtendedPermissions>['mutateAsync'];

  constructor(requestPermissionsMutate: ReturnType<typeof useCronofyRequestExtendedPermissions>['mutateAsync']) {
    this.requestPermissions = requestPermissionsMutate;
  }

  // IMPORTANT: This method opens popup IMMEDIATELY to avoid browser blocking
  // Must be called directly from user click event, before any async operations
  openPermissionsPopupImmediate(): Window | null {
    // Note: in order for Safari to not block the popup, the window.open() call
    // must be triggered by a user action, meaning it must be called from a
    // click event handler and it must not be async
    return this.openPermissionsPopup('about:blank');
  }

  // Method that works with pre-opened popup to avoid browser blocking
  async requestExtendedPermissionsWithPopup(
    calendarIds: string[],
    popup: Window,
    t: TFunction,
  ): Promise<ExtendedPermissionsResult> {
    try {
      const { cronofyCalendarRequestExtendedPermissions } = await this.requestPermissions({ calendarIds });

      if (cronofyCalendarRequestExtendedPermissions.status === 'granted') {
        // Enterprise account - permissions granted automatically
        popup.close();
        return { success: true, automatic: true };
      } else if (cronofyCalendarRequestExtendedPermissions.status === 'pending') {
        // Regular account - need user approval
        const redirectUrl = cronofyCalendarRequestExtendedPermissions.redirect_url;
        if (redirectUrl) {
          // Navigate the popup to the correct URL
          popup.location.href = redirectUrl;
          return await this.handleUserApprovalWithExistingPopup(popup, t);
        }
      }

      popup.close();
      return {
        success: false,
        automatic: false,
        error: cronofyCalendarRequestExtendedPermissions.message,
      };
    } catch (error) {
      console.error('Error requesting extended permissions:', error);
      popup.close();
      return {
        success: false,
        automatic: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Method for handling user approval with existing popup (recommended)
  private async handleUserApprovalWithExistingPopup(popup: Window, t: TFunction): Promise<ExtendedPermissionsResult> {
    return new Promise((resolve) => {
      const checkClosed = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkClosed);
            resolve({ success: true, automatic: false });
            return;
          }
        } catch (error) {
          // COOP policy may block access to popup.closed
          // In this case, we'll assume the popup is still open and continue checking
          console.warn('Cannot access popup.closed due to COOP policy, continuing to check...');
        }
      }, 1000);

      // Fallback timeout - assume success after 5 minutes
      setTimeout(
        () => {
          clearInterval(checkClosed);
          try {
            if (!popup.closed) {
              popup.close();
            }
          } catch (error) {
            console.warn('Cannot close popup due to COOP policy');
          }
          resolve({ success: true, automatic: false });
        },
        5 * 60 * 1000,
      );
    });
  }

  private openPermissionsPopup(url: string): Window | null {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    // Note: in order for Safari to not block the popup, the window.open() call
    // must be triggered by a user action, meaning it must be called from a
    // click event handler and it must not be async
    return window.open(
      url,
      'cronofy_extended_permissions',
      `scrollbars=yes,resizable=yes,width=${width},height=${height},left=${left},top=${top}`,
    );
  }
}

export const getCalendarsNeedingPermissions = (permissions: CronofyCalendarPermission[]): string[] => {
  return permissions.filter((cal) => !cal.hasUnrestrictedAccess && !cal.error).map((cal) => cal.calendarId);
};

export const hasUnrestrictedPermissions = (
  permissions: CronofyCalendarPermission[],
  calendarIds: string[],
): boolean => {
  return calendarIds.every((id) => {
    const permission = permissions.find((p) => p.calendarId === id);
    return permission?.hasUnrestrictedAccess || false;
  });
};
